import React, { useRef } from "react";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import * as yup from "yup";
import { Formik } from "formik";
import UserPhoto from "../../../../components/user-photo/user-photo";
import DropdownMain from "../../../../components/dropdown-main/DropdownMain";
import { FluentMultiselectList } from "../../../../components/FluentUI/Fluent-MultiSelect-List";
import "./admin-user-create-panel.scss";

const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;
const NAME_REGEX = /^[^~`!@#$%^*(){}:;"<>?/[\]\|\\]*$/i;

const AdminUserCreateForm = ({
  context,
  currentUser,
  loaded,
  user,
  saveUser,
  onDepartmentSelected,
  setUser,
  onProfilePictureUploaded,
  clearImage,
  showHidePassword,
  onCancel,
  disableSubDepartment,
}) => {
  const uploadElement = useRef(null);
  const removeElement = useRef(null);

  const onKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const enterKeySaveSubmission = (keyEvent, submitForm) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      submitForm && submitForm();
    }
  };

  const enterKeyPhotoSubmission = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      uploadElement.current.click();
    }
  };

  const enterKeyRemovePhotoSubmission = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      removeElement.current.click();
    }
  };

  const enterKeyCancel = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      onCancel();
    }
  };

  return (
    <Formik
      key={loaded}
      validationSchema={
        // Under is NOT SSO and ( Create Mode || same user)
        !currentUser.profile.isSSO && (!user.isEdit || user.Id == context?.userID)
          ? yup.object({
              Name: yup
                .string()
                .required("Full Name Required")
                .max(255, "Name must be under 255 characters")
                .matches(
                  NAME_REGEX,
                  "Please do not enter any special characters in name"
                ),
              Email: yup
                .string()
                .email("Invalid email address")
                .required("Email Required"),
              CompanyId: yup
                .number()
                .moreThan(0, "Invalid Department")
                .required("Department Required"),
              RoleId: yup
                .number()
                .moreThan(0, "Invalid Role")
                .required("Role Required"),
              Department: yup.string().typeError("Invalid Sub-Department").nullable(),
              ProfileImage: yup.string().typeError("Invalid Profile Image").nullable(),
              Password: yup
                .string()
                .min(6, "Password must be between 6-26 characters in length")
                .max(26, "Password must be between 6-26 characters in length")
                .required("Password Required")
                .nullable(),
              passwordConfirmation: yup
                .string()
                .oneOf([yup.ref("Password")], "Passwords must match")
                .required("Passwords must match")
                .nullable(),
              UserName: yup
                .string()
                .required("Username Required")
            })
          : yup.object({
              Name: yup
                .string()
                .required("Full Name Required")
                .max(255, "Name must be under 255 characters")
                .matches(
                  NAME_REGEX,
                  "Please do not enter any special characters in name"
                ),
              Email: yup
                .string()
                .email("Invalid email address")
                .required("Email Required"),
              CompanyId: yup
                .number()
                .moreThan(0, "Invalid Department")
                .required("Department Required"),
              RoleId: yup
                .number()
                .moreThan(0, "Invalid Role")
                .required("Role Required"),
              Department: yup.string().typeError("Invalid Sub-Department").nullable(),
              ProfileImage: yup.string().typeError("Invalid Profile Image").nullable(),
              UserName: yup
                .string()
                .required("Username Required")
            })
      }
      onSubmit={(values, actions) => {
        // Failsafe: Formik.validationSchema not working as expected here. I am adding back password in case they are null
        if (values.Password == null) {
          if (currentUser?.profile?.password != null) {
            values.Password = currentUser.profile.password;
          }
        }
        if (values.passwordConfirmation == null) {
          if (currentUser?.profile?.passwordConfirmation != null) {
            values.passwordConfirmation =
              currentUser.profile.passwordConfirmation;
          }
        }

        saveUser(values, actions);
      }}
      initialValues={{
        ...user,
        subDepartment: user.selectedSubDepartments || [],
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        submitForm,
      }) => (
        <Form
          noValidate
          className="user-creation-container"
          onSubmit={handleSubmit}
          onKeyDown={onKeyDown}
        >
          <Form.Row className="left-row">
            <Form.Group className="user-full-name">
              <Form.Label>
                Full Name <span style={{ color: "red" }}> * </span>
              </Form.Label>
              <Form.Control
                id="Name"
                name="Name"
                placeholder="Enter Full Name"
                value={values.Name}
                onChange={(e) => {
                  handleChange(e);
                }}
                onBlur={handleBlur}
                isInvalid={touched.Name && errors.Name}
              />
              <Form.Control.Feedback type="invalid" className="inline-error">
                {errors.Name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="user-email-id">
              <Form.Label>
                Email ID <span style={{ color: "red" }}> * </span>
              </Form.Label>
              <Form.Control
                id="Email"
                name="Email"
                type="email"
                placeholder="Enter Your Email"
                value={values.Email}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.Email && errors.Email}
              />
              <Form.Control.Feedback type="invalid" className="inline-error">
                {errors.Email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="user-department">
              <Form.Label>
                Department <span style={{ color: "red" }}> * </span>
              </Form.Label>
              <DropdownMain
                values={
                  user?.allDepartments?.map((department) => {
                    return {
                      id: department.Id,
                      name:
                        department.Name +
                        (department.Active ? "" : " [Inactive]"),
                      disabled: !department.Active,
                    };
                  })
                }
                dropdownButtonText={"Select a Department"}
                onSelect={(selectedValue) => {
                  setFieldValue("CompanyId", selectedValue.id);
                  setFieldTouched("CompanyId");
                  onDepartmentSelected(selectedValue, setFieldValue);
                }}
                currentSelected={user.selectedDepartment || null}
                disabled={!currentUser?.profile?.isSuperAdminRole}
              />
              {!user.selectedDepartment && touched.CompanyId && (
                <div className="inline-error department-error">
                  {errors.CompanyId}
                </div>
              )}
            </Form.Group>

              <Form.Group className="user-sub-department">
                <Form.Label>Sub-Department(s)</Form.Label>
                <FluentMultiselectList
                  key={user.selectedDepartment ? `${user.selectedDepartment.id}` : 'no-department'}
                  placeholder="Select Sub-Department"
                  options={user.subDepartments}
                  label="Sub-Department"
                  required={false}
                  onChange={(selectedOptions) => {
                    setFieldValue('subDepartment', selectedOptions);
                  }}
                  selectedValues={values.subDepartment || []}
                  displayLabel={false}
                  loading={user.isLoadingSubDepartments}
                  disabled={isEmptyObject(user.selectedDepartment) || disableSubDepartment}
                  skipDisabledItems={true}
                  isFluent={false}
                />
              </Form.Group>

            <Form.Group className="user-role">
              <Form.Label>
                Role <span style={{ color: "red" }}> * </span>
              </Form.Label>
              <DropdownMain
                values={
                  user?.allRoles?.map((role) => {
                    return {
                      id: role.Id,
                      name: role.Name,
                      disabled: !role.Active,
                    };
                  })
                }
                dropdownButtonText={"Select Role"}
                onSelect={(selectedValue) => {
                  setFieldValue("RoleId", selectedValue.id);
                  setFieldTouched("RoleId");
                  setUser({ ...user, selectedRole: selectedValue });
                }}
                currentSelected={user.selectedRole || null}
                disabled={isEmptyObject(user.selectedDepartment)}
              />
              {!user.selectedRole && touched.RoleId && (
                <div className="inline-error department-error">
                  {errors.RoleId}
                </div>
              )}
            </Form.Group>
          </Form.Row>
          <Form.Row className="right-row">
            <Form.Group className="user-picture">
              <UserPhoto
                key={user.ProfileImage}
                photoBase64={user.isPhotoSet ? user.profileImageRaw : ""}
                photo={user.isPhotoSet ? user.ProfileImage : ""}
                name={""}
                className="picture-icon"
              />

              <div className="choose-img-btn-container">
                <Button
                  className="btn-wide choose-img-button"
                  onKeyDown={(keyEvent) => enterKeyPhotoSubmission(keyEvent)}
                >
                  <Form.Control
                    name="user-photo"
                    id="user-photo"
                    label="Upload Profile Image"
                    className="upload-image-button"
                    type="file"
                    ref={(upload) => (uploadElement.current = upload)}
                    onChange={(e) => {
                      onProfilePictureUploaded(e);
                    }}
                  />
                  <div className="label-no-spacing">
                    {user.isPhotoSet ? "Change Photo" : "Upload Photo"}
                  </div>
                </Button>
                {user.isPhotoSet && (
                  <Button
                    onKeyDown={(keyEvent) =>
                      enterKeyRemovePhotoSubmission(keyEvent)
                    }
                    ref={(remove) => (removeElement.current = remove)}
                    className="btn-wide btn-outlined clear choose-img-button"
                    onClick={(e) => {
                      clearImage(e);
                    }}
                  >
                    Remove Photo
                  </Button>
                )}
              </div>
            </Form.Group>
            <Form.Group className="user-name">
              <Form.Label className="user-name-label">
                User Name (Windows Login ID){" "}
                <span style={{ color: "red" }}> * </span>
              </Form.Label>
              <Form.Control
                autoComplete="new-password"
                id="UserName"
                placeholder="Enter User Name"
                value={values.UserName}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.UserName && errors.UserName}
              />
              <Form.Control.Feedback type="invalid" className="inline-error">
                {errors.UserName}
              </Form.Control.Feedback>
            </Form.Group>
            {!currentUser.profile.isSSO &&
            (!user.isEdit || values.Id == context?.userID) ? (
              <Form.Group className="password-fields">
                <Form.Group className="password">
                  <Form.Label>
                    Password <span style={{ color: "red" }}> * </span>
                  </Form.Label>
                  <Button
                    className="view-password-icon"
                    onClick={(e) => {
                      showHidePassword(e, "password");
                    }}
                  />
                  <Form.Control
                    autoComplete="new-password"
                    type={user.passwordType}
                    name="Password"
                    id="Password"
                    placeholder="Password"
                    value={values.Password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.Password && errors.Password}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className="inline-error"
                  >
                    {errors.Password}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="confirm-password">
                  <Form.Label>
                    Confirm Password <span style={{ color: "red" }}> * </span>
                  </Form.Label>
                  <Button
                    className="view-password-icon"
                    onClick={(e) => {
                      showHidePassword(e, "confpassword");
                    }}
                  />
                  <Form.Control
                    autoComplete="new-password"
                    type={user.confPasswordType}
                    id="passwordConfirmation"
                    disabled={
                      values.Password === "" || values.Password === undefined
                    }
                    placeholder="Confirm Password"
                    value={values.passwordConfirmation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      values.Password !== "" && errors.passwordConfirmation
                    }
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className="inline-error"
                  >
                    {errors.passwordConfirmation}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Group>
            ) : (
              <></>
            )}
            {user.isEdit ? (
              <Form.Group
                className={
                  currentUser.profile.isSSO
                    ? "user-edit-status user-edit-status-sso"
                    : "user-edit-status"
                }
              >
                <Form.Label> User Status </Form.Label>
                <Form.Check
                  className="user-active-status"
                  type="checkbox"
                  name="formHorizontalRadios"
                  checked={user.Active ? user.Active : false}
                  onChange={() => {
                    setUser({ ...user, Active: true });
                    values.Active = true;
                  }}
                  label="Active"
                />
                <Form.Check
                  className="user-inactive-status"
                  type="checkbox"
                  name="formHorizontalRadios"
                  checked={!user.Active ? !user.Active : false}
                  onChange={() => {
                    setUser({ ...user, Active: false });
                    values.Active = false;
                  }}
                  label="Inactive"
                />
              </Form.Group>
            ) : (
              <></>
            )}
          </Form.Row>
          <Form.Row>
            <ButtonToolbar className="user-btn-container">
              <Button
                className="btn-wide"
                type="submit"
                disabled={isSubmitting}
                onKeyDown={(keyEvent) =>
                  enterKeySaveSubmission(keyEvent, submitForm)
                }
              >
                {user.isEdit ? "Save" : "Create"}
              </Button>             
              <Button className="btn-wide btn-outlined"
                onKeyDown={(keyEvent) => enterKeyCancel(keyEvent)}
                onClick={onCancel}> Cancel </Button>
            </ButtonToolbar>
          </Form.Row>
        </Form>
      )}
    </Formik>
  );
};

AdminUserCreateForm.propTypes = {
  context: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  loaded: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  saveUser: PropTypes.func.isRequired,
  onDepartmentSelected: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  onProfilePictureUploaded: PropTypes.func.isRequired,
  clearImage: PropTypes.func.isRequired,
  showHidePassword: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disableSubDepartment: PropTypes.bool.isRequired,
};

export default AdminUserCreateForm;
